import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Save, Settings2, RefreshCcw, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { errMsg, fmtNumber } from '../lib/format';

const Num = ({ value, onChange, disabled, testid, step = '0.01', suffix }) => (
  <div className="relative">
    <Input
      type="number"
      step={step}
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value))}
      className="text-right font-mono pr-8"
      data-testid={testid}
    />
    {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-muted-foreground pointer-events-none">{suffix}</span>}
  </div>
);

const ScopeDefaults = ({ title, values, onChange, disabled, prefix }) => (
  <div className="space-y-2">
    <div className="text-[12px] uppercase tracking-wide text-muted-foreground font-medium">{title}</div>
    <div className="grid grid-cols-3 gap-3">
      <div className="space-y-1">
        <Label className="text-[12px]">% Indirectos</Label>
        <Num value={values.indirect_pct} disabled={disabled} testid={`${prefix}-ind`} suffix="%" onChange={(v) => onChange({ ...values, indirect_pct: v })} />
      </div>
      <div className="space-y-1">
        <Label className="text-[12px]">% Financiamiento</Label>
        <Num value={values.financing_pct} disabled={disabled} testid={`${prefix}-fin`} suffix="%" onChange={(v) => onChange({ ...values, financing_pct: v })} />
      </div>
      <div className="space-y-1">
        <Label className="text-[12px]">% Utilidad</Label>
        <Num value={values.utility_pct} disabled={disabled} testid={`${prefix}-uti`} suffix="%" onChange={(v) => onChange({ ...values, utility_pct: v })} />
      </div>
    </div>
  </div>
);

export default function ConfiguracionPage() {
  const { canFinalize } = useAuth(); // admin o editor
  const queryClient = useQueryClient();
  const readOnly = !canFinalize;
  const [form, setForm] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['app-settings'],
    queryFn: async () => (await api.get('/settings')).data,
  });

  useEffect(() => {
    if (settings) setForm(JSON.parse(JSON.stringify(settings)));
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: async () => (await api.put('/settings', form)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-settings'] });
      toast.success('Configuración guardada. Se aplicará a nuevas cotizaciones y conceptos.');
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const fetchFx = async () => {
    setFxLoading(true);
    try {
      const { data } = await api.get('/exchange-rate');
      setForm((f) => ({ ...f, exchange_rate_default: data.rate }));
      toast.success(`Tipo de cambio obtenido: ${fmtNumber(data.rate, 4)} MXN/USD`);
    } catch (e) {
      toast.error(errMsg(e, 'No se pudo obtener el tipo de cambio'));
    } finally {
      setFxLoading(false);
    }
  };

  if (isLoading || !form) {
    return <div className="space-y-4"><Skeleton className="h-16" /><Skeleton className="h-64" /><Skeleton className="h-64" /></div>;
  }

  const setScope = (key, scope) => (values) => setForm((f) => ({ ...f, [key]: { ...f[key], [scope]: values } }));

  return (
    <div className="space-y-5 max-w-4xl" data-testid="configuracion-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2"><Settings2 size={22} className="text-primary" /> Configuración</h2>
          <p className="text-sm text-muted-foreground">Parámetros por defecto para cotizaciones y conceptos, en un solo lugar</p>
        </div>
        <Button onClick={() => saveMutation.mutate()} disabled={readOnly || saveMutation.isPending} data-testid="save-settings-button">
          <Save size={15} className="mr-1.5" /> Guardar configuración
        </Button>
      </div>

      {readOnly && (
        <div className="flex items-start gap-2 rounded-lg border border-[hsl(var(--warning)/0.35)] bg-[hsl(var(--warning)/0.08)] p-3 text-[13px]" data-testid="settings-readonly-notice">
          <Info size={15} className="text-[hsl(var(--warning))] mt-0.5 shrink-0" />
          <span>Tu rol (capturista) solo permite consultar la configuración. Los cambios requieren rol Editor o Administrador.</span>
        </div>
      )}

      {/* Parámetros generales */}
      <Card className="border-border shadow-[var(--shadow-elev-1)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Parámetros generales</CardTitle>
          <CardDescription>Se aplican al crear nuevas cotizaciones (cada cotización puede ajustarlos después).</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Tipo de cambio por defecto (MXN/USD)</Label>
            <div className="flex gap-2">
              <Num value={form.exchange_rate_default} disabled={readOnly} testid="settings-tc-input" step="0.0001" onChange={(v) => setForm((f) => ({ ...f, exchange_rate_default: v }))} />
              <Button variant="outline" size="icon" className="shrink-0" onClick={fetchFx} disabled={readOnly || fxLoading} data-testid="settings-fetch-fx-button" title="Obtener de Banxico/API pública">
                <RefreshCcw size={14} className={fxLoading ? 'animate-spin' : ''} />
              </Button>
            </div>
            <p className="text-[12px] text-muted-foreground">Se usa como respaldo manual si la API de Banxico no responde.</p>
          </div>
          <div className="space-y-1">
            <Label>IVA por defecto</Label>
            <Num value={form.iva_default} disabled={readOnly} testid="settings-iva-input" suffix="%" onChange={(v) => setForm((f) => ({ ...f, iva_default: v }))} />
          </div>
        </CardContent>
      </Card>

      {/* Defaults de conceptos */}
      <Card className="border-border shadow-[var(--shadow-elev-1)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Porcentajes por defecto de conceptos (APU)</CardTitle>
          <CardDescription>
            Se aplican automáticamente a cada concepto nuevo (indirectos → financiamiento → utilidad en cascada sobre el costo directo).
            Cada concepto puede ajustarse individualmente en el editor.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <div className="text-[13px] font-medium">Aplicar a conceptos nuevos</div>
              <div className="text-[12px] text-muted-foreground">Prellenar estos porcentajes al capturar conceptos en cotizaciones y catálogo</div>
            </div>
            <Switch
              checked={!!form.apply_defaults_to_new_concepts}
              disabled={readOnly}
              onCheckedChange={(v) => setForm((f) => ({ ...f, apply_defaults_to_new_concepts: v }))}
              data-testid="settings-apply-defaults-switch"
            />
          </div>
          <ScopeDefaults title="Prefabricados" prefix="settings-concept-prefab" values={form.concept_defaults.prefab} disabled={readOnly} onChange={setScope('concept_defaults', 'prefab')} />
          <Separator />
          <ScopeDefaults title="Fotovoltaico (PV)" prefix="settings-concept-pv" values={form.concept_defaults.pv} disabled={readOnly} onChange={setScope('concept_defaults', 'pv')} />
        </CardContent>
      </Card>

      {/* Defaults globales */}
      <Card className="border-border shadow-[var(--shadow-elev-1)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Indirectos globales (adicional, por cotización)</CardTitle>
          <CardDescription>
            Valores sugeridos al activar &ldquo;Indirectos globales&rdquo; en una cotización. La cascada global se aplica <strong>sobre el subtotal de conceptos</strong> y
            se desglosa explícitamente en el resumen, PDF, Excel y portal del cliente — los precios unitarios de los conceptos no cambian.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <ScopeDefaults title="Prefabricados" prefix="settings-global-prefab" values={form.global_defaults.prefab} disabled={readOnly} onChange={setScope('global_defaults', 'prefab')} />
          <Separator />
          <ScopeDefaults title="Fotovoltaico (PV)" prefix="settings-global-pv" values={form.global_defaults.pv} disabled={readOnly} onChange={setScope('global_defaults', 'pv')} />
        </CardContent>
      </Card>

      <p className="text-[12px] text-muted-foreground">
        Los cambios aplican a cotizaciones y conceptos <strong>nuevos</strong>; las cotizaciones existentes conservan sus parámetros y pueden editarse desde su editor
        (panel derecho: TC, IVA, globales · tabla central: %Ind/%Fin/%Util por concepto).
      </p>
    </div>
  );
}
